package com.klantenstop.web.components

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.klantenstop.web.bindings.Icon
import com.klantenstop.web.bindings.L
import com.klantenstop.web.bindings.LeafletState
import com.klantenstop.web.bindings.MapPin
import com.klantenstop.web.bindings.createLeafletMap
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxSize
import com.varabyte.kobweb.compose.ui.modifiers.id
import com.varabyte.kobweb.compose.ui.modifiers.position
import com.varabyte.kobweb.compose.ui.modifiers.size
import com.varabyte.kobweb.compose.ui.modifiers.zIndex
import com.varabyte.kobweb.compose.ui.toAttrs
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.dom.Div
import kotlin.random.Random

@Composable
fun ClusteredMap(
    lat: Double,
    lng: Double,
    label: String,
    iconUrl: String? = null,
    popUpContent: com.klantenstop.web.bindings.PopupContent? = null,
    zoom: Int = 20
) {
    ClusteredMap(
        pins = listOf(MapPin(lat, lng, label, iconUrl, popUpContent)),
        zoom = zoom
    )
}

@Composable
fun ClusteredMap(
    pins: List<MapPin>,
    zoom: Int = 20,
    radius: RadiusConfig? = null,
    mapId: String = remember { "map-${Random.nextInt()}" }
) {
    var leafletState by remember { mutableStateOf<LeafletState?>(null) }

    Box(
        modifier = Modifier
            .fillMaxSize()
            .zIndex(0)
            .position(Position.Relative)
    ) {
        // Map div
        Div(
            attrs = Modifier
                .id(mapId)
                .size(100.percent)
                .toAttrs()
        )
    }

    DisposableEffect(mapId) {
        leafletState = createLeafletMap(mapId)
        onDispose { leafletState = null }
    }

    LaunchedEffect(pins, leafletState, zoom, radius) {
        val state = leafletState ?: return@LaunchedEffect

        updateMarkers(
            state = state,
            pins = pins,
            zoom = zoom,
            radius = radius
        )
    }
}

data class RadiusConfig(
    val centerLat: Double,
    val centerLng: Double,
    val range: Int
)

private fun updateMarkers(
    state: LeafletState,
    pins: List<MapPin>,
    zoom: Int,
    radius: RadiusConfig?
) {
    val icons = pins
        .filter { it.iconUrl != null }
        .distinctBy { it.iconUrl }
        .associate { it.iconUrl!! to createMapIcon(it.iconUrl) }

    // --- Update markers ---
    state.clusters.clearLayers()
    pins.forEach {
        val options = icons[it.iconUrl]?.let(::markerOptions)
        val marker = L.marker(arrayOf(it.lat, it.lng), options)

        // Bind popup based on content type (exhaustive sealed type handling)
        when (val popup = it.popUpContent) {
            is com.klantenstop.web.bindings.PopupContent.Element -> marker.bindPopup(popup.element)
            is com.klantenstop.web.bindings.PopupContent.Html -> marker.bindPopup(popup.html)
            null -> Unit // No popup
        }

        state.clusters.addLayer(marker)
    }

    state.radiusCircle?.let { state.map.removeLayer(it) }
    state.radiusCircle = null

    val circle = radius?.let {
        L.circle(
            arrayOf(it.centerLat, it.centerLng),
            js("{ color: '#5E4200', fillOpacity: 0.2 }")
        ).also { c ->
            c.setRadius(it.range)
            c.addTo(state.map)
            state.radiusCircle = c
        }
    }

    val noAnimate = js("{ animate: false }")
    when {
        // 1️⃣ Radius always wins
        circle != null -> {
            state.map.fitBounds(circle.getBounds(), noAnimate)
        }

        // 2️⃣ Single pin, no radius
        pins.size == 1 -> {
            val pinLatLng = pins.first().let { arrayOf(it.lat, it.lng) }
            state.map.setView(pinLatLng, zoom, noAnimate)
        }

        // 3️⃣ Multiple pins, no radius
        pins.isNotEmpty() -> {
            val points = pins.map { arrayOf(it.lat, it.lng) }.toTypedArray()
            val bounds = L.latLngBounds(points)
            state.map.fitBounds(bounds, noAnimate)
        }

        // 4️⃣ No pins, no radius → keep initial center
        else -> Unit
    }
}

private fun markerOptions(icon: Icon): dynamic {
    val options = js("{}")
    options.icon = icon
    return options
}

fun createMapIcon(
    iconUrl: String,
    iconSize: Pair<Int, Int> = 36 to 36,
    iconAnchor: Pair<Int, Int> = iconSize.first / 2 to iconSize.second,
    popupAnchor: Pair<Int, Int> = 0 to -iconSize.second
): Icon {
    val options = js("{}")
    options.iconUrl = iconUrl
    options.iconSize = arrayOf(iconSize.first, iconSize.second)
    options.iconAnchor = arrayOf(iconAnchor.first, iconAnchor.second)
    options.popupAnchor = arrayOf(popupAnchor.first, popupAnchor.second)

    return L.icon(options)
}
